import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, ShieldAlert, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export interface ApprovalCenterAction {
  id: string;
  title: string;
  description: string;
  preview?: string;
  diff?: { before?: string; after?: string };
  impact?: string;
  recordCount?: number;
  rollbackPossible?: boolean;
  skillId?: string;
  action?: string;
  params?: Record<string, unknown>;
  requiresDoubleConfirm?: boolean;
}

export interface ApprovalCenterProps {
  open: boolean;
  action: ApprovalCenterAction | null;
  loading?: boolean;
  onApprove: () => void | Promise<void>;
  onCancel: () => void;
  className?: string;
}

export function ApprovalCenter({
  open,
  action,
  loading = false,
  onApprove,
  onCancel,
  className,
}: ApprovalCenterProps) {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!open) setConfirmed(false);
  }, [open, action?.id]);

  if (!open || !action) return null;

  const needsDouble = Boolean(action.requiresDoubleConfirm);
  const canApprove = !needsDouble || confirmed;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[80] flex items-end justify-center sm:items-center",
        className,
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="approval-center-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Cerrar"
        onClick={onCancel}
        disabled={loading}
      />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border bg-background shadow-2xl sm:rounded-2xl">
        <div className="border-b px-4 py-3">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <h2 id="approval-center-title" className="text-sm font-semibold leading-tight">
                {action.title}
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">{action.description}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-xs">
          {action.preview != null && action.preview !== "" && (
            <section>
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Vista previa
              </p>
              <pre className="overflow-x-auto rounded-lg bg-muted/60 p-2 whitespace-pre-wrap break-words">
                {action.preview}
              </pre>
            </section>
          )}

          {(action.diff?.before != null || action.diff?.after != null) && (
            <section>
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Cambios
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-lg border bg-red-50/50 p-2 dark:bg-red-950/20">
                  <p className="mb-1 text-[10px] font-medium text-red-700 dark:text-red-400">
                    Antes
                  </p>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words text-foreground/80">
                    {action.diff.before ?? "—"}
                  </pre>
                </div>
                <div className="rounded-lg border bg-emerald-50/50 p-2 dark:bg-emerald-950/20">
                  <p className="mb-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
                    Después
                  </p>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words text-foreground/80">
                    {action.diff.after ?? "—"}
                  </pre>
                </div>
              </div>
            </section>
          )}

          {action.impact && (
            <section>
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Impacto
              </p>
              <p className="text-foreground/80">{action.impact}</p>
            </section>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {typeof action.recordCount === "number" && (
              <Badge variant="secondary" className="font-normal">
                {action.recordCount} registro{action.recordCount === 1 ? "" : "s"}
              </Badge>
            )}
            {typeof action.rollbackPossible === "boolean" && (
              <Badge
                variant={action.rollbackPossible ? "outline" : "destructive"}
                className="gap-1 font-normal"
              >
                <Undo2 className="h-3 w-3" />
                {action.rollbackPossible ? "Se puede deshacer" : "No se puede deshacer"}
              </Badge>
            )}
          </div>

          {needsDouble && (
            <label className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-2.5">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                disabled={loading}
                className="mt-0.5"
              />
              <span className="text-xs leading-snug">
                Confirmo eliminación
                <span className="mt-0.5 block text-[10px] text-muted-foreground">
                  Esta acción puede ser irreversible.
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="space-y-1 px-4 pt-2 text-[10px] text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Qué cambia:</span> solo lo indicado en esta propuesta.
          </p>
          <p>
            <span className="font-medium text-foreground">Qué no cambia:</span> el resto de tu catálogo y configuración.
          </p>
          <p>Tu siguiente paso es aprobar el cambio o cancelar. Después vas a ver adónde seguir.</p>
        </div>
        <div className="flex gap-2 border-t px-4 py-3">
          <Button
            size="sm"
            className="flex-1"
            disabled={loading || !canApprove}
            onClick={() => void onApprove()}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Aprobar"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            disabled={loading}
            onClick={onCancel}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}

type ResolveFn = (approved: boolean) => void;

export function useApprovalCenter() {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<ApprovalCenterAction | null>(null);
  const resolveRef = useRef<ResolveFn | null>(null);

  const ask = useCallback((next: ApprovalCenterAction) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setAction(next);
      setOpen(true);
    });
  }, []);

  const resolve = useCallback((approved: boolean) => {
    resolveRef.current?.(approved);
    resolveRef.current = null;
    setOpen(false);
    setAction(null);
  }, []);

  const cancel = useCallback(() => {
    resolve(false);
  }, [resolve]);

  return { open, action, ask, resolve, cancel };
}
